// Phase 3 R9: draw a signature (Settings → Report Branding → Signature). A window with a white pad and a baseline;
// mouse, finger or pen (pointer events; touch-action none so the page doesn't scroll); Undo / Clear / Cancel / Save
// (Save disabled while the pad is empty); on phones (≤ 640 px) the window fills the screen. Save = a transparent PNG
// trimmed to the strokes (long edge ≤ 600 px), ink #1d2433, handed to onSave (which uploads it like an uploaded
// signature). A failed save keeps the window open with the strokes.
const T = (k, p) => window.HUD?.T?.(k, p) || k;
export const SIG_INK = '#1d2433';
export const SIG_MAX_EDGE = 600;
const PAD = 8; // px of margin kept around the strokes when trimming

const CSS = `
.rb3-sp-ov{position:fixed;inset:0;background:rgba(15,20,25,.45);display:flex;align-items:center;justify-content:center;padding:16px;z-index:10050}
.rb3-sp{background:#fff;color:var(--navy,#1a2e2b);border-radius:14px;width:min(640px,100%);padding:16px 18px;box-shadow:0 12px 40px rgba(0,0,0,.25);box-sizing:border-box}
.rb3-sp-h{display:flex;justify-content:space-between;align-items:center;gap:8px;margin-bottom:4px}
.rb3-sp-h h3{margin:0;font-size:1rem;font-weight:700}
.rb3-sp-x{border:none;background:none;font-size:1.3rem;line-height:1;color:var(--slate,#667085);cursor:pointer;padding:4px 6px}
.rb3-sp-hint{color:var(--slate,#667085);font-size:.82rem}
.rb3-sp-pad{position:relative;background:#fff;border:1px solid var(--mint-l,#dfe5e5);border-radius:10px;height:220px;margin:10px 0 12px;overflow:hidden}
.rb3-sp-pad canvas{position:absolute;inset:0;width:100%;height:100%;touch-action:none;cursor:crosshair}
.rb3-sp-base{position:absolute;left:24px;right:24px;bottom:56px;border-bottom:1.5px solid #c9d1d4;pointer-events:none}
.rb3-sp-ph{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;color:#a5b0b5;font-size:.88rem;pointer-events:none}
.rb3-sp-f{display:flex;justify-content:space-between;gap:8px;flex-wrap:wrap}
.rb3-sp-f>div{display:flex;gap:8px;flex-wrap:wrap}
.rb3-sp .btn:disabled{opacity:.45;cursor:default}
@media (max-width:640px){
  .rb3-sp-ov{padding:0;align-items:stretch}
  .rb3-sp{border-radius:0;width:100%;height:100dvh;display:flex;flex-direction:column;padding:14px 16px calc(14px + env(safe-area-inset-bottom))}
  .rb3-sp-pad{flex:1;height:auto;min-height:160px}
}`;
function injectCss() {
  if (document.getElementById('rb3-sp-css')) return;
  const s = document.createElement('style'); s.id = 'rb3-sp-css'; s.textContent = CSS; document.head.appendChild(s);
}

function paint(ctx, strokes) {
  ctx.lineCap = 'round'; ctx.lineJoin = 'round'; ctx.strokeStyle = SIG_INK;
  for (const s of strokes) {
    ctx.beginPath();
    s.forEach((p, i) => { ctx.lineWidth = p.w; if (i) ctx.lineTo(p.x, p.y); else ctx.moveTo(p.x, p.y); });
    if (s.length === 1) ctx.lineTo(s[0].x + 0.1, s[0].y); // a dot
    ctx.stroke();
  }
}

// The strokes (CSS px) → a transparent PNG canvas trimmed to their box (+ margin), long edge ≤ 600 px (never upscaled
// beyond 3×). → HTMLCanvasElement, or null when there are no strokes.
export function strokesToCanvas(strokes) {
  const pts = strokes.flat(); if (!pts.length) return null;
  const xs = pts.map(p => p.x), ys = pts.map(p => p.y), wmax = Math.max(...pts.map(p => p.w));
  const m = PAD + wmax / 2;
  const x0 = Math.min(...xs) - m, y0 = Math.min(...ys) - m, w = Math.max(...xs) + m - x0, h = Math.max(...ys) + m - y0;
  const sc = Math.min(3, SIG_MAX_EDGE / Math.max(w, h));
  const o = document.createElement('canvas');
  o.width = Math.max(1, Math.min(SIG_MAX_EDGE, Math.round(w * sc))); o.height = Math.max(1, Math.min(SIG_MAX_EDGE, Math.round(h * sc)));
  const c = o.getContext('2d');
  c.scale(o.width / w, o.height / h); c.translate(-x0, -y0);
  paint(c, strokes);
  return o;
}

// Open the pad. onSave(blob) uploads it (throws on failure). → Promise<boolean>: true = saved, false = closed.
export function openSignaturePad({ onSave }) {
  injectCss();
  return new Promise(resolve => {
    const ov = document.createElement('div'); ov.className = 'rb3-sp-ov';
    const box = document.createElement('div'); box.className = 'rb3-sp'; box.setAttribute('role', 'dialog'); box.setAttribute('aria-modal', 'true');
    const head = document.createElement('div'); head.className = 'rb3-sp-h';
    const h3 = document.createElement('h3'); h3.id = 'rb3-sp-title'; h3.textContent = T('rb3_sig_pad_title'); box.setAttribute('aria-labelledby', h3.id);
    const x = document.createElement('button'); x.type = 'button'; x.className = 'rb3-sp-x'; x.textContent = '×'; x.title = T('btn_close'); x.setAttribute('aria-label', T('btn_close'));
    head.append(h3, x);
    const hint = document.createElement('div'); hint.className = 'rb3-sp-hint'; hint.textContent = T('rb3_sig_pad_hint');
    const pad = document.createElement('div'); pad.className = 'rb3-sp-pad';
    const base = document.createElement('div'); base.className = 'rb3-sp-base';
    const ph = document.createElement('div'); ph.className = 'rb3-sp-ph'; ph.textContent = T('rb3_sig_pad_ph');
    const cv = document.createElement('canvas'); cv.className = 'rb3-sp-cv';
    pad.append(base, ph, cv);
    const foot = document.createElement('div'); foot.className = 'rb3-sp-f';
    const mk = (label, cls, extra) => { const b = document.createElement('button'); b.type = 'button'; b.className = 'btn btn-sm ' + cls + ' ' + extra; b.textContent = label; return b; };
    const undo = mk(T('rb3_sig_undo'), 'btn-secondary', 'rb3-sp-undo'), clear = mk(T('rb3_sig_clear'), 'btn-secondary', 'rb3-sp-clear');
    const cancel = mk(T('btn_cancel'), 'btn-secondary', 'rb3-sp-cancel'), save = mk(T('rb3_sig_save'), 'btn-primary', 'rb3-sp-save');
    const l = document.createElement('div'), r = document.createElement('div'); l.append(undo, clear); r.append(cancel, save); foot.append(l, r);
    box.append(head, hint, pad, foot); ov.appendChild(box); document.body.appendChild(ov);

    const ctx = cv.getContext('2d');
    let strokes = [], cur = null, curId = null, busy = false;
    const redraw = () => {
      const rc = cv.getBoundingClientRect();
      ctx.clearRect(0, 0, rc.width, rc.height); paint(ctx, strokes);
      ph.style.display = strokes.length ? 'none' : 'flex';
      save.disabled = busy || !strokes.length;
    };
    const size = () => { const rc = cv.getBoundingClientRect(), d = window.devicePixelRatio || 1; cv.width = Math.round(rc.width * d); cv.height = Math.round(rc.height * d); ctx.setTransform(d, 0, 0, d, 0, 0); redraw(); };
    const pt = e => { const rc = cv.getBoundingClientRect(); return { x: e.clientX - rc.left, y: e.clientY - rc.top, w: e.pointerType === 'pen' && e.pressure ? 1.2 + e.pressure * 2.4 : 2.4 }; };
    cv.addEventListener('pointerdown', e => {
      if (busy || cur || (e.pointerType === 'mouse' && e.button !== 0)) return;
      e.preventDefault(); try { cv.setPointerCapture(e.pointerId); } catch (_) {}
      cur = [pt(e)]; curId = e.pointerId; strokes.push(cur); redraw();
    });
    cv.addEventListener('pointermove', e => { if (!cur || e.pointerId !== curId) return; e.preventDefault(); cur.push(pt(e)); redraw(); });
    const end = e => { if (e.pointerId === curId) { cur = null; curId = null; } };
    cv.addEventListener('pointerup', end); cv.addEventListener('pointercancel', end);

    const onResize = () => size();
    const onKey = e => { if (e.key === 'Escape' && !busy) { e.preventDefault(); close(false); } };
    function close(saved) { window.removeEventListener('resize', onResize); document.removeEventListener('keydown', onKey, true); ov.remove(); resolve(saved); }
    window.addEventListener('resize', onResize); document.addEventListener('keydown', onKey, true);
    undo.onclick = () => { if (busy) return; strokes.pop(); redraw(); };
    clear.onclick = () => { if (busy) return; strokes = []; redraw(); };
    cancel.onclick = x.onclick = () => { if (!busy) close(false); };
    save.onclick = async () => {
      if (busy || !strokes.length) return;
      const o = strokesToCanvas(strokes);
      const blob = await new Promise(res => o.toBlob(res, 'image/png'));
      busy = true; redraw();
      try { await onSave(blob); close(true); }
      catch (e) { console.error('❌ signature save:', e); busy = false; redraw(); window.HUD?.toast?.(T('rb3_sig_save_failed'), 'error'); }
    };
    requestAnimationFrame(size);
  });
}
